import { useState, useEffect, useMemo } from 'react';

/**
 * props:
 * - data (grids array)
 * - currentPrice (opcional, preço atual do mercado)
 */
function GridTable(props) {

    const [grids, setGrids] = useState([]);

    useEffect(() => {
        if (!props.data || !props.data.length) return;
        setGrids(props.data);
    }, [props.data])

    function getPriceFromConditions(conditions) {
        if (!conditions || typeof conditions !== 'string') return null;

        // Preço-alvo do nível: primeiro current.bestAsk / current.bestBid
        const current = conditions.match(/current\.best(?:Ask|Bid)\s*[<>]=?\s*([0-9]*\.?[0-9]+(?:[eE][+-]?[0-9]+)?)/);
        if (current) return parseFloat(current[1]);

        const any = conditions.match(/[<>]=?\s*([0-9]*\.?[0-9]+(?:[eE][+-]?[0-9]+)?)/);
        return any ? parseFloat(any[1]) : null;
    }

    function getGridSide(grid) {
        const side = grid.orderTemplate?.side || grid.OrderTemplate?.side;
        if (side === 'BUY' || side === 'SELL') return side;

        const conditions = grid.conditions || '';
        if (conditions.includes('bestAsk')) return 'BUY';
        if (conditions.includes('bestBid')) return 'SELL';
        return null;
    }

    const { buyGrids, sellGrids } = useMemo(() => {
        if (!grids || !grids.length) return { buyGrids: [], sellGrids: [] };

        const mapped = grids.map(g => ({
            ...g,
            side: getGridSide(g),
            price: getPriceFromConditions(g.conditions),
            quantity: g.orderTemplate?.quantity || g.OrderTemplate?.quantity || 'N/A'
        }));

        const buy = mapped
            .filter(g => g.side === 'BUY')
            .sort((a, b) => (b.price || 0) - (a.price || 0));

        const sell = mapped
            .filter(g => g.side === 'SELL')
            .sort((a, b) => (a.price || 0) - (b.price || 0));

        return { buyGrids: buy, sellGrids: sell };
    }, [grids]);

    function formatPrice(price) {
        const number = parseFloat(price);
        if (!Number.isFinite(number)) return 'N/A';
        return number.toFixed(8).replace(/\.?0+$/, '');
    }

    return (
        <div className="row">
            {props.error && (
                <div className="col-12 mb-3">
                    <div className="alert alert-warning py-2 mb-0">{props.error}</div>
                </div>
            )}
            <div className="col-12 mb-3">
                <h6 className="text-muted">Total de Grids: {grids.length} ({buyGrids.length} compra / {sellGrids.length} venda)</h6>
            </div>
            
            {/* Grids de VENDA (acima do preço atual) */}
            <div className="col-md-6 mb-3">
                <div className="card">
                    <div className="card-header bg-success text-white">
                        <strong>Grids de VENDA ({sellGrids.length})</strong>
                        <small className="ms-2">Ordens acima do preço atual</small>
                    </div>
                    <div className="card-body p-0">
                        {sellGrids.length > 0 ? (
                            <div className="table-responsive" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                                <table className="table table-sm table-hover mb-0">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th>#</th>
                                            <th>Preço</th>
                                            <th>Quantidade</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sellGrids.map((grid, index) => (
                                            <tr key={grid.id || `sell-${index}`} className="table-success">
                                                <td>{index + 1}</td>
                                                <td>
                                                    <strong>{formatPrice(grid.price)}</strong>
                                                    {props.currentPrice && grid.price && (
                                                        <small className="text-muted d-block">
                                                            +{((grid.price / props.currentPrice - 1) * 100).toFixed(2)}%
                                                        </small>
                                                    )}
                                                </td>
                                                <td>{grid.quantity}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="p-3 text-center text-muted">
                                <small>Nenhum grid de venda ativo</small>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Grids de COMPRA (abaixo do preço atual) */}
            <div className="col-md-6 mb-3">
                <div className="card">
                    <div className="card-header bg-warning text-dark">
                        <strong>Grids de COMPRA ({buyGrids.length})</strong>
                        <small className="ms-2">Ordens abaixo do preço atual</small>
                    </div>
                    <div className="card-body p-0">
                        {buyGrids.length > 0 ? (
                            <div className="table-responsive" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                                <table className="table table-sm table-hover mb-0">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th>#</th>
                                            <th>Preço</th>
                                            <th>Quantidade</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {buyGrids.map((grid, index) => (
                                            <tr key={grid.id || `buy-${index}`} className="table-warning">
                                                <td>{index + 1}</td>
                                                <td>
                                                    <strong>{formatPrice(grid.price)}</strong>
                                                    {props.currentPrice && grid.price && (
                                                        <small className="text-muted d-block">
                                                            {((grid.price / props.currentPrice - 1) * 100).toFixed(2)}%
                                                        </small>
                                                    )}
                                                </td>
                                                <td>{grid.quantity}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="p-3 text-center text-muted">
                                <small>Nenhum grid de compra ativo</small>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default GridTable;
